import { z } from "zod";
export const OUR_STORY_PRODUCTION_SESSION_CONTRACT = "OUR_STORY_PRODUCTION_SESSION_V1";
const safeId = z
    .string()
    .trim()
    .min(1)
    .max(240)
    .regex(/^[A-Za-z0-9][A-Za-z0-9_.:/-]*$/u);
const timestamp = z.string().datetime({ offset: true });
const sha256 = z.string().regex(/^[a-f0-9]{64}$/u);
export const ourStoryProductionPrincipalV1Schema = z
    .object({
    principalId: safeId,
    actorId: safeId,
    displayName: z.string().trim().min(1).max(160),
    consentReceiptId: safeId.nullable(),
    consentedAt: timestamp.nullable(),
    individualApprovedRevision: z.number().int().positive().nullable(),
    individualApprovedAt: timestamp.nullable(),
    jointApprovedRevision: z.number().int().positive().nullable(),
    jointApprovedAt: timestamp.nullable(),
})
    .strict();
export const ourStoryRevealedSourceV1Schema = z
    .object({
    sourceId: safeId,
    principalId: safeId,
    title: z.string().trim().min(1).max(240),
    text: z.string().trim().min(1).max(30_000),
    sourceDigestSha256: sha256,
    revealedAt: timestamp,
    revealedBy: safeId,
    consentReceiptId: safeId,
})
    .strict();
export const ourStoryProductionSessionV1Schema = z
    .object({
    contract: z.literal(OUR_STORY_PRODUCTION_SESSION_CONTRACT),
    bookId: safeId,
    sourceRevision: z.number().int().nonnegative(),
    revision: z.number().int().positive(),
    principals: z.tuple([ourStoryProductionPrincipalV1Schema, ourStoryProductionPrincipalV1Schema]),
    revealPolicy: z.literal("EXPLICIT_PRINCIPAL_CONSENT_BEFORE_SHARED_STORAGE"),
    disagreementTreatment: z.enum([
        "PRESERVE_SIDE_BY_SIDE",
        "PRESERVE_ATTRIBUTED_UNRESOLVED",
        "EXCLUDE_BY_JOINT_AGREEMENT",
    ]),
    revealedSources: z.array(ourStoryRevealedSourceV1Schema).max(8),
    state: z.enum([
        "AWAITING_CONSENT",
        "CAPTURING_REVEALED_SOURCES",
        "AWAITING_INDIVIDUAL_APPROVALS",
        "AWAITING_JOINT_APPROVALS",
        "APPROVED",
    ]),
    createdAt: timestamp,
    updatedAt: timestamp,
    truthBoundary: z.literal("ONLY_EXPLICITLY_REVEALED_ATTRIBUTED_SOURCES_NO_WITHHELD_CONTENT_STORED_OR_PROJECTED"),
})
    .strict()
    .superRefine((session, context) => {
    const [principalA, principalB] = session.principals;
    if (principalA.principalId === principalB.principalId ||
        principalA.actorId === principalB.actorId)
        context.addIssue({
            code: "custom",
            path: ["principals"],
            message: "Our Story requires two distinct authenticated principals.",
        });
    const principalIds = new Set(session.principals.map(({ principalId }) => principalId));
    if (session.revealedSources.some(({ principalId }) => !principalIds.has(principalId)))
        context.addIssue({
            code: "custom",
            path: ["revealedSources"],
            message: "Every revealed source must retain one exact principal attribution.",
        });
    if (new Set(session.revealedSources.map(({ sourceId }) => sourceId)).size !==
        session.revealedSources.length)
        context.addIssue({
            code: "custom",
            path: ["revealedSources"],
            message: "Revealed source identifiers must be unique.",
        });
    if (session.state === "APPROVED" &&
        session.principals.some(({ consentReceiptId, individualApprovedRevision, jointApprovedRevision }) => !consentReceiptId || !individualApprovedRevision || !jointApprovedRevision))
        context.addIssue({
            code: "custom",
            path: ["state"],
            message: "Approved Our Story sessions require both consents and both approval rounds.",
        });
});
const commandBase = z.object({
    expectedBookRevision: z.number().int().nonnegative(),
    expectedSessionRevision: z.number().int().nonnegative(),
});
export const updateOurStoryProductionSessionCommandSchema = z.discriminatedUnion("action", [
    commandBase
        .extend({
        action: z.literal("CREATE_SESSION"),
        principals: z.tuple([
            z
                .object({
                principalId: safeId,
                actorId: safeId,
                displayName: z.string().trim().min(1).max(160),
            })
                .strict(),
            z
                .object({
                principalId: safeId,
                actorId: safeId,
                displayName: z.string().trim().min(1).max(160),
            })
                .strict(),
        ]),
    })
        .strict(),
    commandBase.extend({ action: z.literal("GIVE_CONSENT") }).strict(),
    commandBase
        .extend({
        action: z.literal("REVEAL_SOURCE"),
        sourceId: safeId,
        title: z.string().trim().min(1).max(240),
        text: z.string().trim().min(1).max(30_000),
    })
        .strict(),
    commandBase
        .extend({
        action: z.literal("SET_DISAGREEMENT_TREATMENT"),
        treatment: z.enum([
            "PRESERVE_SIDE_BY_SIDE",
            "PRESERVE_ATTRIBUTED_UNRESOLVED",
            "EXCLUDE_BY_JOINT_AGREEMENT",
        ]),
    })
        .strict(),
    commandBase.extend({ action: z.literal("APPROVE_INDIVIDUAL") }).strict(),
    commandBase.extend({ action: z.literal("APPROVE_JOINT") }).strict(),
]);
//# sourceMappingURL=our-story-production-session.js.map